import moment, { MomentInput } from "moment";
import ObjectModel from "../prototypes/ObjectModel";
import CategoryCollection from "./CategoryCollection";
import { BehaviorSubject } from "rxjs";
import ConfigurationCollection from "./ConfigurationCollection";
import UrlFactory from "../prototypes/UrlFactory";
import ProductCollection from "./ProductCollection";
import AllergenGroupCollection from "./AllergenGroupCollection";
import TagCollection from "./TagCollection";
import CartModel from "./CartModel";
import WidgetGroupCollection from "./WidgetGroupCollection";
import CustomerModel from "./CustomerModel";
import CountryCollection from "./CountryCollection";
import reduxStore from "@src/store";
import { cartSlice } from "@src/store/reducers/CartSlice";
import { isValidCartStoreId } from "@src/store/reducers/CartSlice/selectors";
import TranslationModel from "./TranslationModel";
import { datadogRum } from "@datadog/browser-rum";
import { dataDogLogger } from "@src/lib/datadog/initDatadogLogs";
import { RSError } from "@src/lib/errors";
import MessageCollection from "./MessageCollection";
import StoreModel from "./StoreModel";
import PaymentBrandCollection from "./PaymentBrandCollection";
import PaymentGroupCollection from "./PaymentGroupCollection";
import AddressCollection from "@src/backbone/model/AddressCollection";
import PostcodeModel from "@src/backbone/model/PostcodeModel";
import PostcodeCollection from "@src/backbone/model/PostcodeCollection";
import PlaceModel from "@src/backbone/model/PlaceModel";
import AddressModel from "@src/backbone/model/AddressModel";
import {
  mainCartInstance,
  mainCustomerInstance,
  rollingStartNavigation,
} from "../provider/ModelProvider";
import ProductModel from "./ProductModel";
import { IAlertOptions, IConfirmOptions } from "../interface/Alert";
import { IDisplayModalContentType } from "@src/lib/alert-notice/AlertNoticeContext";
import PaymentModeModel from "@src/backbone/model/PaymentModeModel";
import PaymentModeCollection from "@src/backbone/model/PaymentModeCollection";
import { AppSlice } from "@src/store/reducers/AppSlice";
import StoreCollection from "@src/backbone/model/StoreCollection";
import { slugify } from "@src/backbone/prototypes/stringHelpers";
import hasWindow from "@src/utils/hasWindow";
import { customerSlice } from "@src/store/reducers/CustomerSlice";
import { Collection, Model } from "backbone";
import BannerCollection from "./BannerCollection";
import { startTransition } from "react";
import { BaseAxiosApi } from "@src/hooks/useAxiosInstance";
import {
  GATSBY_CONF_BAGUETTES_REUTILISABLES,
  GATSBY_CONF_BAGUETTES_REUTILISABLES_WARN,
  GATSBY_CONF_BAGUETTES_REUTILISABLES_WARN_DELAY,
  GATSBY_CONF_BAGUETTES_REUTILISABLES_WARN_MODAL,
  GATSBY_CONF_GOOGLE_ADDRESS_SEPARATOR,
  GATSBY_CONF_HOME_DISPLAY_OTHER_CONTRY,
  GATSBY_CONF_LUNCH_DINER_SERVICE_THRESHOLD_TIME,
  GATSBY_CONF_MOD_POSTCODE,
  GATSBY_CONF_MOD_POSTCODE_REGEX,
  GATSBY_CONF_SHOP_NAME,
} from "@src/utils/constants";
import MacaronModel from "./MacaronModel";
import MacaronCollection from "./MacaronCollection";
import { Target } from "../interface/target";

declare global {
  interface Window {
    cart: any;
  }
}

export type AppAttributes = {
  category?: CategoryCollection;
  configuration?: ConfigurationCollection;
  cart?: CartModel;
  product?: ProductCollection;
  allergengroup?: AllergenGroupCollection;
  tag?: TagCollection;
  widgetGroup?: WidgetGroupCollection;
  customer?: CustomerModel;
  country?: CountryCollection;
  translation?: TranslationModel;
  message?: MessageCollection;
  paymentBrand?: PaymentBrandCollection;
  paymentGroup?: PaymentGroupCollection;
  paymentMode?: PaymentModeCollection;
  news?: Collection<Model>;
  banners?: BannerCollection;
  macaron?: MacaronCollection;
  [_e: string]: any;
};

// export var app: AppModel = new AppModel(); // singleton reference
const _appObjects$ = new BehaviorSubject<AppAttributes | undefined>(undefined);
export const appObjects$ = _appObjects$.asObservable();
let initialScrollTop = 0;
/** Évite double scheduling / setupReady (setupObjects appelé 2×). */
let setupCartScheduled = false;
let setupCartExecuted = false;
let setupReadyDispatched = false;

class AppModel extends ObjectModel<AppAttributes> {
  name = "AppModel";
  private static instance: AppModel;
  route = "";
  private authInterceptorRegistered = false;
  defaults() {
    return {
      scrollLocked: false,
      scrollTop: initialScrollTop,
      deviceType: false,
    };
  }

  public static getInstance(): AppModel {
    return app;
  }

  initialize() {
    // app = this;
    // _app$.next(app);
    // app.initApp();
    // TODO: set router argument
    // UrlFactory?.setup(app);
    this.registerCustomerAuthInterceptor();
  }
  setupObjects(objects: any) {
    // LogExecutionTime("app setupObjects");
    const payload = { ...objects };
    // Ne pas écraser le bootstrap critique par `configuration: undefined` (setupweb).
    if (payload.configuration == null) {
      delete payload.configuration;
    }
    app.set(payload);
    // traductions disponibles : vérifie les clés demandées avant le setup
    if (payload.translation instanceof TranslationModel) {
      TranslationModel.flushPendingMissingKeys(payload.translation);
    }
    app.getPostcodes(); // initial localStorage fetch

    // TODO: implement setup getPaymentGroups
    app.getPaymentGroups()?.setup();

    // TODO: implement setupInternational
    // app.getStoresInt().setupInternational();

    const cart = app.getCart();
    const store = cart.getStore();
    const customer = app.getCustomer();
    const addresses = app.getAddresses();

    // global cart reference
    if (hasWindow()) {
      window.cart = cart;
    }

    customer.on("login logout", function (e: any) {
      // quand on change d'utilisateur, on recharge toutes les adresses
      addresses.reset();
      if (customer.isLogged()) {
        // si l'utilisateur est identifié, on charge ses adresses
        addresses.remoteFetch();
      }
    });

    app.on("X-Customer-authenticated", function (state: string) {
      // TODO: implement customer fetching
      const isLogged = customer.isLogged(),
        requireFetch = state === (isLogged ? "no" : "yes");
      if (requireFetch && !customer.isRemoteFetching) {
        if (isLogged) {
          customer.clear();
          customer.trigger("externalLogout");
        } else {
          customer.remoteFetch();
        }
      }
    });

    // TODO: implement externalLogout
    customer.on("externalLogout", function () {
      app.confirm(
        {
          title: app.t("Attention", "UI"),
          subtitle: app.t("Vous avez été déconnecté", "Authentification"),
          cancel: app.t("Ok", "UI"),
          validation: app.t("Me reconnecter", "Authentification"),
        },
        (confirm: any) => {
          if (confirm) {
            app.go("authentification");
          }
        }
      );
    });

    // TODO: implement set cart store_closed
    // app.on("X-Store-closed", function (state: string) {
    //   cart.set("store_closed", state === "yes");
    // });

    // TODO: implement TagCommander setup
    // TagCommander.setup(app);

    function SetupCart() {
      if (setupCartExecuted) return;
      setupCartExecuted = true;
      // LogExecutionTime('Setup Cart', 6);
      cart.setup(() => {
        if (setupReadyDispatched) return;
        setupReadyDispatched = true;
        const dispatchSetupReady = () => {
          // startTransition : évite React #421 (update Suspense pendant hydratation).
          startTransition(() => {
            reduxStore.dispatch(AppSlice.actions.setSetupReady(true));
            reduxStore.dispatch(AppSlice.actions.notifyBackboneRevision());
          });

          if (objects.customer) {
            startTransition(() => {
              reduxStore.dispatch(
                customerSlice.actions.setCurrentCustomer(
                  objects.customer.attributes
                )
              );
            });
          }
          if (hasWindow()) {
            // @ts-ignore
            window.app = app;
          }

          startTransition(() => {
            app.trigger("setupReady", app);
          });
        };

        // Double rAF : après peinture / hydratation Suspense (évite #421 listing).
        if (typeof window !== "undefined") {
          requestAnimationFrame(() => {
            requestAnimationFrame(dispatchSetupReady);
          });
        } else {
          dispatchSetupReady();
        }
      });
    }

    const pendingStoreId = cart.get("id_store");

    // Redux tôt : hasLocation / prix dès id_store localStorage (avant store.sync).
    if (isValidCartStoreId(pendingStoreId)) {
      startTransition(() => {
        reduxStore.dispatch(
          cartSlice.actions.updateCurrentCart(cart.attributes)
        );
      });
    }

    const scheduleSetupCart = () => {
      if (setupCartScheduled) return;

      const resolvedStore = cart.getStore();
      if (resolvedStore) {
        setupCartScheduled = true;
        // Prix-FIX: ne plus bloquer SetupCart/storecontext derrière store.fetch sync.
        SetupCart();
        resolvedStore.fetch({
          error: () => {},
        });
        return;
      }

      // "0" n'est pas un id_store valide — ne pas bloquer SetupCart.
      if (isValidCartStoreId(pendingStoreId)) {
        // id_store connu mais store pas encore dans la collection — réessayer
        // quand les stores arrivent (2e setupObjects / reset collection).
        const stores = app.getStores?.() || app.get("store");
        if (stores && typeof stores.once === "function") {
          stores.once("update reset sync", function () {
            scheduleSetupCart();
          });
        }
        return;
      }

      setupCartScheduled = true;
      SetupCart();
    };

    scheduleSetupCart();
  }

  private registerCustomerAuthInterceptor() {
    if (this.authInterceptorRegistered) {
      return;
    }
    this.authInterceptorRegistered = true;

    const handleHeader = (
      headers?: Record<string, string | string[] | undefined>
    ) => {
      if (!headers) return;
      const headerKey = Object.keys(headers).find(
        (key) => key.toLowerCase() === "x-customer-authenticated"
      );
      if (!headerKey) return;
      const headerValue = headers[headerKey];
      const rawValue = Array.isArray(headerValue)
        ? headerValue[0]
        : headerValue;
      if (typeof rawValue === "string") {
        const value = rawValue.trim().toLowerCase();
        if (value) {
          this.trigger("X-Customer-authenticated", value);
        }
      }
    };

    BaseAxiosApi.interceptors.response.use(
      (response) => {
        handleHeader(
          response.headers as Record<string, string | string[] | undefined>
        );
        return response;
      },
      (error) => {
        if (error?.response?.headers) {
          handleHeader(
            error.response.headers as Record<
              string,
              string | string[] | undefined
            >
          );
        }
        return Promise.reject(error);
      }
    );
  }
  getShopName() {
    return GATSBY_CONF_SHOP_NAME;
  }
  getIngredients() {
    return app.get("ingredient");
  }
  getIngredient(id_ingredient: any) {
    return app.getIngredients()?.get(id_ingredient);
  }
  getTags() {
    return app.get("tag") || new TagCollection([]);
  }
  getAddresses(): AddressCollection {
    const rawAddresses = app.get("address");
    if (!rawAddresses) {
      const newAddresses = new AddressCollection([]);
      app.set("address", newAddresses);
      return newAddresses;
    }
    return rawAddresses;
  }
  getAddress(
    id_address: string,
    includeDeleted: boolean = false
  ): AddressModel | undefined {
    if (!id_address) return;
    id_address = id_address.toString();
    const params: any = { id_address };
    if (!includeDeleted) {
      params.deleted = 0;
    }
    const addresses = app.getAddresses();
    return addresses?.findWhere(params);
  }
  getAddressSeparator(noSpace?: boolean) {
    let separator = GATSBY_CONF_GOOGLE_ADDRESS_SEPARATOR;
    if (!/ $/.test(separator)) {
      // on force l'espace après la virgule si il n'existe pas
      separator += noSpace ? "" : " ";
    }
    return separator;
  }
  getAllergenGroups(): AllergenGroupCollection {
    return app.get("allergengroup") || new AllergenGroupCollection([]);
  }
  getBanner(id_banner: string) {
    const banners = app.getBanners();
    return banners ? banners.findWhere({ id_banner: id_banner }) : undefined;
  }
  getBanners(): BannerCollection {
    return app.get("banner");
  }
  getCategory(id_category: any) {
    return app.getCategories().get(id_category);
  }
  getCategories(): CategoryCollection {
    const categories = app.get("category");
    return !!categories ? categories : new CategoryCollection([]);
  }
  //
  // attention la notion de slug n'existe pas vraiment sur le model category
  // elle correspond à un identifiant géré via Configuration
  //
  getConfigurations(): ConfigurationCollection {
    return app.get("configuration") || new ConfigurationCollection([]);
  }
  getMacarons() {
    return app.get("macaron");
  }
  getMacaron(id_macaron: any) {
    return (
      app.getMacarons()?.get(id_macaron) || new MacaronModel({ id_macaron })
    );
  }
  getProduct(id_product: string | number): ProductModel | undefined {
    return app.getProducts().get(id_product);
  }
  getProducts(): ProductCollection {
    const productsCollection = this.get("product");
    return !!productsCollection
      ? productsCollection
      : new ProductCollection([]);
  }
  getPostcodes(): PostcodeCollection {
    let postcodes = app.get("postcode");
    if (!postcodes) {
      postcodes = new PostcodeCollection();
      app.set("postcode", postcodes);
    }
    return postcodes;
  }
  getPostcode(id_postcode?: string) {
    return !!id_postcode ? app.getPostcodes().get(id_postcode) : undefined;
  }
  getPostcodeByValue(value: string): PostcodeModel | undefined {
    value = value.toUpperCase();
    const postcodes = app.getPostcodes();
    if (!postcodes) return;
    let postalCode = value;

    if (GATSBY_CONF_MOD_POSTCODE) {
      let exec;
      try {
        // eslint-disable-next-line no-eval
        const postcodeRegExp = eval(GATSBY_CONF_MOD_POSTCODE_REGEX);
        if (postcodeRegExp instanceof RegExp) {
          exec = postcodeRegExp.exec(value);
        } else {
          throw new Error("invalid _MOD_POSTCODE_REGEX_");
        }
      } catch (err) {
        // Tracing (sévérité "error") : défaut de config/regex non bloquant pour l'UX
        // (le formatage postcode est simplement ignoré), mais révélateur d'un vrai bug.
        app.trace(err, { GATSBY_CONF_MOD_POSTCODE }, "error");
      }
      if (GATSBY_CONF_MOD_POSTCODE === "with_suffix") {
        if (exec) {
          // converti la saisie via l’expression régulière
          postalCode = exec[1];
          if (postalCode === "") {
            // cas spécial du luxembourg ou il y a un seul code postal avec des quartiers
            // il suffit de configurer une expression régulière permissive
            return postcodes.first();
          }
        } else {
          // si le code postal ne match pas
          // avec la syntaxe de l'expression régulière
          // on ne renvoie pas de résultat
          return;
        }
      }
    }
    return postcodes.findWhere({ postcodeUpperCase: postalCode });
  }
  getDistrict(id_district: any) {
    // District is not implemented as a Backbone Model, raw JS Object is returned
    // TODO: implement
    // @ts-ignore
    return app.getPostcodes().getDistrict(id_district);
  }
  getPlace(
    place_id: string,
    defaultModel?: AddressModel | PlaceModel
  ): PlaceModel | undefined {
    return PlaceModel.getPlace(place_id, defaultModel);
  }
  getStores(): StoreCollection {
    return app.get("store");
  }
  getStore(id_store: string): StoreModel | undefined {
    return app.getStores()?.get(id_store);
  }
  getTranslations() {
    return app.get("translation") || new TranslationModel();
  }
  getPaymentGroups(): PaymentGroupCollection | undefined {
    return app.get("paymentGroup");
  }
  getPaymentBrands(): PaymentBrandCollection {
    return app.get("paymentBrand") || new PaymentBrandCollection([]);
  }
  getPaymentMode(
    id_payment_mode: string | number
  ): PaymentModeModel | undefined {
    return app.getPaymentModes()?.get(id_payment_mode);
  }
  getPaymentModes() {
    return app.get("paymentMode");
  }
  getCustomer(): CustomerModel {
    return app.get("customer") || mainCustomerInstance;
  }
  getCart(): CartModel {
    return app.get("cart") || mainCartInstance;
  }
  getMessages(): MessageCollection {
    const messages = app.get("message");
    // Guard : setup peut avoir posé un ObjectCollection générique / un Model
    // si l’instanciation MessageCollection a échoué.
    if (messages instanceof MessageCollection) {
      return messages;
    }
    return new MessageCollection([]);
  }
  getMessage(
    location: "home_top_banner" | "listing_top_banner" | "",
    options?: { messages?: MessageCollection }
  ) {
    const messages =
      !!options && options.messages instanceof MessageCollection
        ? options.messages
        : app.getMessages();
    return messages.getByLocation(location, options);
  }
  // translate alias
  t(
    value: string,
    context: string,
    parameters?: string | number | boolean | Array<string | number | boolean>
  ) {
    return app.getTranslations().translate(value, context, parameters);
  }

  // c(…) est un alias pour getConfigurations().getValue(…)
  c<Type extends string | number | boolean | undefined = string>(
    key: string,
    defaultValue?: Type,
    type?: "string" | "number" | "boolean"
  ): Type {
    return app.getConfigurations().getValue<Type>(key, defaultValue, type);
  }
  //
  isNowLunch(datetime?: MomentInput) {
    if (!datetime) datetime = new Date();
    const parts = /^(\d+):(\d+)/.exec(
        GATSBY_CONF_LUNCH_DINER_SERVICE_THRESHOLD_TIME
      ),
      hours = (parts && parseInt(parts[1])) || 0,
      minutes = (parts && parseInt(parts[2])) || 0,
      m = moment(datetime),
      mh = m.hours();
    return mh < hours || (mh <= hours && m.minutes() <= minutes);
  }
  hasNotCustomerPrimaryAddress() {
    return !app.getCustomer().getPrimaryAddress();
  }
  error(message?: any, context?: object) {
    const messageText = typeof message === "string" ? message : String(message);

    // Erreur applicative générique : on force une stack trace exploitable
    // (message ou Error déjà présente) pour le debug, comme le faisait
    // ErrorModel.getTrace() côté legacy.
    const error = message instanceof Error ? message : new RSError(messageText);
    // Une RSError porte déjà son contexte (constructeur) ; l'argument `context`
    // explicite reste possible pour les erreurs génériques non typées.
    const errorContext = context ?? (error instanceof RSError ? error.context : undefined);
    datadogRum.addError(error, errorContext ? { context: errorContext } : undefined);
  }
  /**
   * Tracing applicatif non bloquant pour l'UX (ex: donnée manquante mais gérée avec fallback,
   * cas attendu sans alternative…) : envoyé vers DataDog Logs (`dataDogLogger`), pas vers le
   * RUM Error Tracking - contrairement à `error()`, réservé aux vraies erreurs applicatives.
   * `severity` permet de distinguer un simple signal (`"warn"`, défaut) d'un défaut plus
   * sérieux mais toujours non bloquant (`"error"`).
   * (Nommée `trace` et non `warn` : `AppModel.warn()` existe déjà pour l'alerte UI.)
   */
  trace(
    message?: any,
    context?: object,
    severity: "warn" | "error" = "warn"
  ) {
    const messageText = typeof message === "string" ? message : String(message);
    const logContext = context ?? (message instanceof RSError ? message.context : undefined);
    dataDogLogger[severity](
      messageText,
      logContext ? { context: logContext } : undefined
    );
  }
  notify(
    type: string,
    options: IAlertOptions | IConfirmOptions,
    callback?: (...args: any) => void
  ) {
    const validation = app.t("J'ai compris", "UI");
    const cancel = app.t("Annuler", "UI");
    app.trigger(
      type + "Notice",
      { type, validation, cancel, ...options } as
        | IAlertOptions
        | IConfirmOptions,
      callback
    );
  }
  alert(
    options?: IAlertOptions | string,
    callback?: (result?: any, ...args: any) => void
  ) {
    if (!options) {
      let title = "Setup Error",
        subtitle = "please contact support service.";
      try {
        title = app.t("Oups…", "Error");
        subtitle = app.t("Une erreur inconnue semble être apparue.", "Error");
        // TODO: implement
        /* if (errorModel && site_vars.env != "prod") {
          subtitle +=
            '<pre class="alert alert-danger">' +
            errorModel.get("stack") +
            "</pre>";
        } */
      } catch (err) {}
      options = {
        title: title,
        subtitle: subtitle,
      };
    } else if (typeof options === "string") {
      options = {
        subtitle: options,
      };
    }
    app.notify("alert", options as IAlertOptions, callback);
  }

  warn(subtitle: string, callback?: (...args: any) => void) {
    const title = app.t("Attention", "UI");
    app.alert({ title, subtitle }, callback);
  }

  confirm(
    options: IConfirmOptions,
    callback?: (confirm: boolean, ...args: any) => void
  ) {
    app.notify("confirm", options, callback);
  }
  prompt(options: any, callback?: (...args: any) => void) {
    app.notify("prompt", options, callback);
  }
  bottomNotice(message: string) {
    app.notify("bottom", {
      subtitle: message,
    });
  }
  banNotice(options: string) {
    //   TODO: implement
    app.notify("ban", {
      title: "",
      subtitle: options,
    });
  }
  displayModal(view: IDisplayModalContentType) {
    app.trigger("displayModal", view);
  }

  go(
    target: Target,
    options: {
      product?: ProductModel;
      search?: any;
      rewrite?: string;
      closeModalDelay?: number;
    } = {},
    reload?: boolean
  ) {
    let isModal = false;

    if (target.startsWith("http") || target.startsWith("#")) {
      if (typeof window !== "undefined") {
        window.location.href = target;
      }
    } else if (target === "cart") {
      const { cart: cartState } = reduxStore.getState();
      const hasStore =
        !!cartState.currentCart.id_store &&
        cartState.currentCart.id_store !== "0";

      // if no store selected, open delivery options
      if (hasStore) {
        if (!cartState.open) {
          reduxStore.dispatch(cartSlice.actions.setOpenCurrentCart(true));
        }
      } else {
        reduxStore.dispatch(
          cartSlice.actions.openDeliveryOptions({
            redirect: "cart",
          })
        );
      }
    } else {
      let route = "";
      switch (target) {
        case "product":
          if (options.product) {
            // TODO: UrlFactory
            route = UrlFactory.getRoute(
              "product",
              options.product.getUrlOptions(),
              true,
              false
            );
            break;
          }
          break;
        // TODO: UrlFactory for listing
        /*  */
        case "listing":
          route = UrlFactory.getRoute("allCategories", {}, true, false);
          break;
        case "stores":
          app.set("storeLocatorSearch", "");
          // TODO: UrlFactory
          // route = UrlFactory.getRoute(target, {}, true, false);
          break;
        case "category":
        case "authentification":
        case "accountDashboard":
        case "accountOrders":
        case "checkoutOpc":
        case "supportCenter":
        case "supportCenterResource":
        case "supportCenterSearch":
        case "storeLocatorPosition":
        case "storeLocatorBounds":
          // TODO: UrlFactory
          route = UrlFactory.getRoute(target, options || {}, true, false);
          break;
        case "home":
          console.log(
            "GATSBY_CONF_HOME_DISPLAY_OTHER_CONTRY",
            GATSBY_CONF_HOME_DISPLAY_OTHER_CONTRY
          );
          route = UrlFactory.getRoute(
            target,
            options || {},
            true,
            !GATSBY_CONF_HOME_DISPLAY_OTHER_CONTRY
          );
          break;
        case "storeLocatorSearch":
          // slugify la recherche
          var search = slugify(options.search);
          // enregistre la valeur de recherche non formatée au niveau app
          app.set("storeLocatorSearch", options.search);
          // TODO: UrlFactory
          route = UrlFactory.getRoute(target, { search }, true, false);
          break;
        default:
          route = UrlFactory.getRoute(target, {}, true, false);
          break;
      }
      //
      // copy from legacy Backbone router TOTO check this
      let _route = route.startsWith("/") ? route : "/" + route;
      if (rollingStartNavigation) {
        const navigationResult = rollingStartNavigation.navigate(_route, {
          state: {
            modal: isModal,
            previousRoute: isModal ? window.location.pathname : undefined,
            closeModalDelay: options.closeModalDelay || 0,
          },
        });

        if (navigationResult?.then) {
          navigationResult.then(() => {
            if (reload) {
              window.location.reload();
            }
          });
        } else if (reload) {
          window.location.reload();
        }
      } else {
        // intlSushishopNavigate(_route);
      }
      // router.go(route, reload);
    }
  }
  //
  alertReCaptcha(message: any) {
    var opt = {
      title: app.t("Échec vérification de sécurité", "UI"),
      subtitle: message,
      cancel: app.t("Réessayer", "UI"),
      validation: app.t("Contacter le support", "SupportCenter"),
    };
    app.confirm(opt, function (success: any) {
      if (success) {
        app.go("supportCenter");
      }
    });
  }
  getBaguettesReutilisables(): ProductModel | undefined {
    return app.getProduct(GATSBY_CONF_BAGUETTES_REUTILISABLES);
  }
  warnBaguettesReutilisables(onModal?: boolean) {
    const warn = onModal
      ? GATSBY_CONF_BAGUETTES_REUTILISABLES_WARN_MODAL
      : GATSBY_CONF_BAGUETTES_REUTILISABLES_WARN;
    const baguettesReutilisablesProduct = app.getBaguettesReutilisables();
    return (
      warn &&
      !!baguettesReutilisablesProduct &&
      baguettesReutilisablesProduct.isBuyableInContext()
    );
  }

  shouldWarnBaguettesReutilisables(): boolean {
    const warn = app.warnBaguettesReutilisables(true);
    const diff = moment().diff(
      localStorage.BAGUETTES_REUTILISABLES_WARN_DATE || 0,
      "d"
    ); // délai en jours entre deux affichages
    return warn && diff > GATSBY_CONF_BAGUETTES_REUTILISABLES_WARN_DELAY;
  }
}
export var app: AppModel = new AppModel();
export default AppModel;
